import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '@/services/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useT } from '@/store';

const MIN_QUERY_LENGTH = 2;

/**
 * Type an address, pick a suggestion, get a place — the dispatcher-facing
 * front end of /api/geocode/search (integrations/nominatim.py, self-hosted,
 * see that module's docstring for why there is no Google Places key here).
 *
 * WHY IT LIVES IN ui/ RATHER THAN orders/. It shipped inside the order form
 * and stayed there, which meant address search existed for the ONE location
 * a dispatcher types once and never sees again — the one-off delivery — and
 * did not exist for the three lists a carrier fills in on installation day
 * and lives with afterwards: quarries, sites and client companies. Those
 * three took bare decimal coordinates and a free-text address box. Same
 * shape as the pin-picker finding one layer down (see CoordinateField.jsx):
 * the capability was built, and the person doing the typing could not reach
 * it. It now backs all four callers from here.
 *
 * Deliberately dumb about what a "place" is for the caller: this component
 * only ever calls `onSelect({label, lat, lng})`. CoordinateField feeds the
 * coordinates into the two boxes and the map; ClientList takes the label
 * and ignores the coordinates, because a client's registered address is an
 * invoice requisite and not a point on a map. Search is a faster way to
 * fill in the SAME field, never a second source of truth for it.
 *
 * UNCONTROLLED BY DEFAULT — the box holds the query, the caller holds the
 * result (OrderForm, CoordinateField). Pass `value`/`onValueChange` and the
 * box IS the caller's field: what is typed is kept as typed, and picking a
 * suggestion overwrites it with the full address (ClientList). That second
 * mode is why there is no separate "search" box above the address field
 * there — two boxes holding almost the same string is a form that asks
 * which one counts.
 *
 * Styled after DriverAssignField.jsx's search-and-pick pattern (the closest
 * existing precedent for "type to filter, click a result").
 */
export function AddressSearchInput({
  onSelect,
  ariaLabel,
  placeholder,
  value,
  onValueChange,
}) {
  const t = useT();
  const [ownQuery, setOwnQuery] = useState('');
  // `undefined` and not falsiness: an empty string is a perfectly good
  // controlled value, and reading it as "uncontrolled" would strand the
  // caller's field the moment somebody cleared the box.
  const controlled = value !== undefined;
  const query = controlled ? value : ownQuery;
  const setQuery = controlled ? onValueChange : setOwnQuery;

  const [open, setOpen] = useState(false);
  // The address a suggestion was taken for, while the box still holds it.
  // Picking one answers the question the box was asking, and the dropdown
  // must stay shut until something new is typed — including when the box
  // takes focus back a moment later, which is not hypothetical: every caller
  // puts this inside a <label>, and a click on a suggestion hands focus to
  // the labelled control on its way out. Without this the list sprang open
  // again over the very coordinates it had just written, and spent a second
  // geocoder request re-asking for the answer it had.
  const [pickedLabel, setPickedLabel] = useState(null);
  const rootRef = useRef(null);
  const debounced = useDebouncedValue(query, 300);
  const trimmed = debounced.trim();
  const answered = pickedLabel !== null && query === pickedLabel;

  const { data, isFetching } = useQuery({
    queryKey: ['geocode-search', trimmed],
    queryFn: () => api(`/api/geocode/search${qs({ q: trimmed })}`),
    enabled: open && !answered && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 60_000,
  });
  const results = data?.results ?? [];

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(result) {
    // The box first, so a controlled caller that reads its field back out
    // of `value` sees the address rather than the fragment that found it.
    setQuery(result.label);
    setPickedLabel(result.label);
    onSelect?.(result);
    setOpen(false);
  }

  const showDropdown = open && !answered && trimmed.length >= MIN_QUERY_LENGTH;

  return (
    <div className="relative" ref={rootRef}>
      <input
        type="search"
        className="field w-full"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? t('address.searchPlaceholder')}
        aria-label={ariaLabel}
      />

      {showDropdown ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-56 overflow-y-auto
                     rounded-[12px] border border-line bg-panel-2 py-1 shadow-xl"
        >
          {isFetching ? (
            <li className="px-3 py-2 text-[11.5px] text-dim">{t('address.searching')}</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-[11.5px] text-dim">{t('address.noResults')}</li>
          ) : (
            results.map((result, index) => (
              <li key={`${result.lat},${result.lng},${index}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => pick(result)}
                  className="block w-full px-3 py-1.5 text-left text-[11.5px] text-ink hover:bg-white/6"
                >
                  {result.label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
